/**
 * Web post-pair confirm card (Phase 7 — mirrors the Android
 * `PairConfirmCard.kt` and the extension's
 * `apps/extension/src/shared/ui/PairConfirmCardSlot/PairConfirmCard.tsx`). Shown on
 * MainPage for 7 days after pairing when
 * `pairAutoNameResult.collision === false` and the user hasn't
 * dismissed it.
 *
 * Stateless w.r.t. storage — the parent slot owns the visibility
 * decision and the rename API call. This component just renders the
 * auto-name with two actions and an inline rename dialog for the
 * "Edit" path. Action CTAs use the shared `@reflecto/client-ui`
 * `<Button>`; remaining layout/dialog styling lives inline
 * (`React.CSSProperties`), matching `InstallPrompt.tsx` /
 * `UpdatePrompt.tsx` since the web app has no shared stylesheet beyond
 * `global.css`.
 */

import { MAX_DEVICE_NAME_LENGTH } from "@reflecto/client-core";
import { Button } from "@reflecto/client-ui";
import { type CSSProperties, type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { messages } from "../i18n";

export interface PairConfirmCardProps {
  autoName: string;
  onLooksGood: () => void;
  onRename: (newName: string) => Promise<void>;
}

export function PairConfirmCard({ autoName, onLooksGood, onRename }: PairConfirmCardProps) {
  const [editing, setEditing] = useState(false);
  // Restore focus to the Edit button when the rename dialog closes (WCAG 2.1 SC 2.4.3).
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const closeDialog = () => {
    setEditing(false);
    // Defer to the next microtask so React re-renders the section before we focus (sync focus would race the commit).
    queueMicrotask(() => editButtonRef.current?.focus());
  };
  return (
    <>
      <section
        aria-label={messages.pairConfirm.cardAriaLabel}
        data-testid="pair-confirm-card"
        style={cardStyle}
      >
        <p style={messageStyle}>
          {/* Render the auto-name as a real <strong> rather than
           *  baking it into the localised string — accessible
           *  emphasis without requiring locales to repeat the markup
           *  in their templates. The catalog supplies the surrounding
           *  copy as a function so locales can reorder freely. */}
          {renderMessageWithName(messages.pairConfirm.cardMessage, autoName)}
        </p>
        <div style={actionsStyle}>
          <Button variant="primary" size="m" onClick={onLooksGood}>
            {messages.pairConfirm.cardLooksGood}
          </Button>
          <Button variant="secondary" size="m" ref={editButtonRef} onClick={() => setEditing(true)}>
            {messages.pairConfirm.cardEdit}
          </Button>
        </div>
      </section>
      {editing && (
        <PairConfirmRenameDialog
          currentName={autoName}
          onConfirm={async (newName) => {
            await onRename(newName);
            closeDialog();
          }}
          onCancel={closeDialog}
        />
      )}
    </>
  );
}

/**
 * Render a localised message function that produces a string with the
 * auto-name inlined as a sentinel token. We split on the sentinel
 * rather than asking each locale to repeat HTML markup: the catalog
 * function inlines the auto-name with `${name}`; we feed it a unique
 * placeholder the user-visible string would never contain, split the
 * resulting string on that placeholder, and render the middle slot
 * as `<strong>`.
 *
 * Falls back to a flat (no-bold) render if the placeholder isn't
 * found — defensive fallback in case a future locale's template
 * dropped the slot.
 */
function renderMessageWithName(template: (name: string) => string, name: string) {
  // Wrap the sentinel in U+0000 NULL bytes — never produced by `autoNameDevice`'s tier A–C
  // pipeline (only printable UA-derived tokens) so a real device name can never collide
  // with this placeholder, even contrived ones.
  const SENTINEL = "\u0000PAIR_CONFIRM_NAME_SLOT\u0000";
  const rendered = template(SENTINEL);
  const idx = rendered.indexOf(SENTINEL);
  if (idx === -1) {
    return template(name);
  }
  return (
    <>
      {rendered.slice(0, idx)}
      <strong>{name}</strong>
      {rendered.slice(idx + SENTINEL.length)}
    </>
  );
}

interface PairConfirmRenameDialogProps {
  currentName: string;
  onConfirm: (newName: string) => Promise<void>;
  onCancel: () => void;
}

function PairConfirmRenameDialog({
  currentName,
  onConfirm,
  onCancel,
}: PairConfirmRenameDialogProps) {
  const [name, setName] = useState(currentName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  // Focus the input on first mount only. An inline ref callback would fire on every render
  // (each keystroke) and re-issue focus(), which can interrupt IME composition and selection.
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // useId() instead of a hardcoded string so multiple dialogs on the same page (hypothetical
  // future surface composition) can't share an aria-labelledby target.
  const titleId = useId();
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && !saving;

  // Tab focus trap: wrap focus within the dialog's enabled focusable children. Without this,
  // a keyboard user can Tab out of the dialog into the obscured page behind the backdrop.
  const handleTabTrap = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      "button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])"
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    // role="dialog" sits on the inner visible box (not the full-screen backdrop) per
    // WAI-ARIA: the role represents the dialog's content boundary, not the click-shield.
    // The backdrop also catches Escape, Tab (focus trap), and click-outside-to-dismiss —
    // the standard close + focus-containment affordances for modals.
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop catches bubbled events; dialog role is on the inner box.
    <div
      style={dialogBackdropStyle}
      onKeyDown={(e) => {
        if (e.key === "Escape" && !saving) onCancel();
        else handleTabTrap(e);
      }}
      onClick={(e) => {
        // Only dismiss when the click landed on the backdrop itself, not when it bubbled
        // up from a child (e.g. the input or a button inside the dialog).
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        style={dialogStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} style={dialogTitleStyle}>
          {messages.pairConfirm.renameTitle}
        </h2>
        <label style={dialogLabelStyle}>
          <span>{messages.pairConfirm.renameLabel}</span>
          <input
            style={dialogInputStyle}
            type="text"
            value={name}
            maxLength={MAX_DEVICE_NAME_LENGTH}
            onChange={(e) => {
              setName(e.target.value);
              setError(false);
            }}
            disabled={saving}
            ref={inputRef}
          />
        </label>
        {error && <span style={dialogErrorStyle}>{messages.pairConfirm.renameFailed}</span>}
        <div style={dialogActionsStyle}>
          <Button variant="secondary" size="m" onClick={onCancel} disabled={saving}>
            {messages.pairConfirm.renameCancel}
          </Button>
          <Button
            variant="primary"
            size="m"
            loading={saving}
            disabled={!canSave}
            onClick={async () => {
              setSaving(true);
              setError(false);
              try {
                await onConfirm(trimmed);
              } catch {
                setError(true);
              } finally {
                // Always clear in-flight state — parent normally unmounts this dialog after
                // onConfirm resolves, but `finally` removes the implicit dependency on that.
                setSaving(false);
              }
            }}
          >
            {messages.pairConfirm.renameSave}
          </Button>
        </div>
      </div>
    </div>
  );
}

const cardStyle: CSSProperties = {
  borderRadius: 12,
  padding: 16,
  background: "var(--reflecto-surface-container, #f4f4f4)",
  border: "1px solid rgba(0,0,0,0.05)",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  // Web mounts the slot in the bare `header` prop (no `.recapSlot`), so the card owns its inset here.
  margin: "12px 16px 0",
};

const messageStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: "20px",
};

const actionsStyle: CSSProperties = {
  display: "flex",
  gap: 8,
};

const dialogBackdropStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
};

const dialogStyle: CSSProperties = {
  background: "var(--reflecto-surface-container, #ffffff)",
  color: "var(--reflecto-text-primary, #171717)",
  borderRadius: 12,
  padding: 16,
  minWidth: 240,
  maxWidth: 320,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const dialogTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 16,
  lineHeight: "22px",
  fontWeight: 600,
};

const dialogLabelStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 13,
  color: "var(--reflecto-text-secondary, #404040)",
};

const dialogInputStyle: CSSProperties = {
  font: "inherit",
  fontSize: 14,
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--reflecto-border, #d4d4d4)",
};

const dialogErrorStyle: CSSProperties = {
  // Match the fallback used in CollisionDisambiguation.tsx so both rename-error surfaces
  // render the same colour when the design token isn't defined yet.
  color: "var(--reflecto-error, #b00020)",
  fontSize: 12,
};

const dialogActionsStyle: CSSProperties = {
  display: "flex",
  gap: 8,
  justifyContent: "flex-end",
};
